import { defineStore } from 'pinia'

import type { ChatRequestPageContext } from '~/api/shopping-assistant/types'

import type { EntryPoint } from '../composables/useShoppingAssistantContext'

export interface ShoppingAssistantWebSidebarState {
  // Whether the floating/full-screen assistant is shown.
  isOpen: boolean
  // Which CTA opened the assistant. Required — callers must declare intent.
  entryPoint: EntryPoint | null
  // Attribution source for a seeded link. Null when absent.
  entrySource: string | null
  // Seed consumed once by the chat core when it mounts on a fresh open.
  seededMessage: string
  pageContext: ChatRequestPageContext | null
  // Incremented on each restart() so the host can key ShoppingAssistantChat
  // and force a remount without closing the panel.
  conversationKey: number
  // Whether the live conversation holds at least one real user message —
  // reported by ShoppingAssistantChat as it mounts/updates. The single
  // source of truth for "is there something to lose", shared by every
  // entry point that decides whether to confirm before discarding the
  // current chat (the web-sidebar host adapter, the search bar's own "search
  // with AI" flow, a seeded-link relaunch).
  hasUserMessages: boolean
  // Whether the next mount should resume the user's latest server-side chat
  // instead of creating a new one. Set by entry points (pills, search bar)
  // that want to continue the ongoing conversation; defaults to true (resume
  // latest). Entry points that start fresh pass false.
  shouldResumeLatest: boolean
  // A message to inject into the running chat without remounting. Set by
  // entry points (pills) when the sidebar is already open and
  // shouldResumeLatest is true — the host watches it and forwards it to
  // the running chat's receivedEvent. Cleared after consumption.
  pendingMessage: string
}

export interface OpenOptions {
  entryPoint: EntryPoint
  entrySource?: string | null
  message?: string
  pageContext?: ChatRequestPageContext | null
  shouldResumeLatest?: boolean
}

/**
 * Presence state for the web sidebar shopping assistant: open/closed plus the
 * seed handoff from entry points. It is the single seam the header triggers
 * and the web-sidebar host reads. Persists no conversation locally — an
 * unseeded reopen resumes the user's latest server-side chat.
 */
export const useShoppingAssistantWebSidebar = defineStore(
  'shoppingAssistantWebSidebar',
  {
    state: (): ShoppingAssistantWebSidebarState => ({
      isOpen: false,
      entryPoint: null,
      entrySource: null,
      seededMessage: '',
      pageContext: null,
      conversationKey: 0,
      hasUserMessages: false,
      shouldResumeLatest: true,
      pendingMessage: '',
    }),
    actions: {
      setHasUserMessages(value: boolean) {
        this.hasUserMessages = value
      },
      setPendingMessage(message: string) {
        this.pendingMessage = message
      },
      clearPendingMessage() {
        this.pendingMessage = ''
      },
      open({
        entryPoint,
        entrySource = null,
        message = '',
        pageContext = null,
        shouldResumeLatest = true,
      }: OpenOptions) {
        this.isOpen = true
        this.entryPoint = entryPoint
        this.entrySource = entrySource
        this.seededMessage = message
        this.pageContext = pageContext
        this.shouldResumeLatest = shouldResumeLatest
      },
      // Replace the live conversation without closing the panel. The host keys
      // ShoppingAssistantChat on conversationKey, so incrementing it forces a
      // remount without triggering the panel open/close transition.
      restart({
        entryPoint,
        entrySource = null,
        message = '',
        pageContext = null,
        shouldResumeLatest = true,
      }: OpenOptions) {
        this.isOpen = true
        this.conversationKey += 1
        this.entryPoint = entryPoint
        this.entrySource = entrySource
        this.seededMessage = message
        this.pageContext = pageContext
        this.shouldResumeLatest = shouldResumeLatest
      },
      close() {
        this.isOpen = false
        this.entryPoint = null
        this.entrySource = null
        this.seededMessage = ''
        this.pageContext = null
        this.shouldResumeLatest = true
        this.pendingMessage = ''
      },
    },
  },
)
